import { isPostcode, isValidEmail, postcodeExample } from '../../core/rules/contact'
import type { Account } from '../../core/types/account'
import type { Address, Country } from '../../core/types/requests'

/** In de checkout altijd met centen (één definitie, in core). */
export { euroCents } from '../../core/format/currency'

/**
 * De tekstvelden van het adres; ook de volgorde waarin de focus naar een fout springt.
 * Postcode en huisnummer eerst: in Nederland vullen die straat en plaats vanzelf aan.
 * Het land is een keuzelijst.
 */
export type AddressTextKey = Exclude<keyof Address, 'country'>
export const addressKeys: AddressTextKey[] = ['firstName', 'lastName', 'postcode', 'houseNumber', 'street', 'city']

export const countries: { value: Country; label: string }[] = [
  { value: 'NL', label: 'Nederland' },
  { value: 'BE', label: 'België' },
]

export const emptyAddress: Address = {
  firstName: '',
  lastName: '',
  postcode: '',
  houseNumber: '',
  street: '',
  city: '',
  country: 'NL',
}

export const isEmptyAddress = (a: Address) => addressKeys.every((k) => a[k] === '')

/** Het bezorgadres uit het account: dezelfde vorm, plus de naam op het pakket. */
export function addressFromAccount(account: Account): Address {
  const { firstName, lastName, address } = account.customer
  return { firstName, lastName, ...address }
}

/** Velden die de site zelf al controleert, in de volgorde waarin de focus naar een fout springt. */
export const checkedKeys = ['email', ...addressKeys] as const
export type CheckedKey = (typeof checkedKeys)[number]

/**
 * Directe controle per veld, in de toon van Sarah. Leeg = in orde.
 * De server blijft de baas: zijn foutmeldingen komen hier gewoon bovenop.
 */
export function checkField(key: CheckedKey, raw: string, country: Country = 'NL'): string {
  const value = raw.trim()
  switch (key) {
    case 'email':
      if (!value) return 'Vul je e-mailadres in, dan stuur ik je de bevestiging.'
      return isValidEmail(value) ? '' : 'Dit e-mailadres klopt nog niet helemaal. Kijk even naar de @ en de punt.'
    case 'firstName':
      return value ? '' : 'Hoe mag ik je noemen?'
    case 'lastName':
      return value ? '' : 'Je achternaam nog, voor op het pakket.'
    case 'postcode':
      if (!value) return 'Je postcode nog.'
      return isPostcode(value, country) ? '' : `Een postcode in ${country === 'BE' ? 'België' : 'Nederland'} ziet eruit als ${postcodeExample[country]}.`
    case 'houseNumber':
      if (!value) return 'Je huisnummer nog.'
      return /^\d/.test(value) ? '' : 'Een huisnummer begint met een cijfer, bijv. 12 of 12a.'
    case 'street':
      return value ? '' : 'Je straat nog.'
    case 'city':
      return value ? '' : 'Je woonplaats nog.'
  }
}

/** Het id van een veld en van de foutmelding eronder — gedeeld door formulier en focuslogica. */
export const fieldId = (key: string) => `co-${key}`
export const fieldErrorId = (key: string) => `co-${key}-error`
